import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import requester from "web/script/modules/requester";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import storage from "web/script/utils/storage";
import { openSignupOverlay } from "web/redux/ducks/signup-overlay";
import { openEmailCaptureOverlay } from "web/redux/ducks/email-capture-overlay";
import withReduxProvider from "../../redux-provider";
import SVGIcon from "../svg-icon/svg-icon";
import Button from "../button/button";
import styles from "./follow-sticky-banner.css";


const ONE_HOUR = 3600000;

function FollowStickyBanner({
    lystId,
    registrationReason,
    followedText,
    notFollowedText,
    isAuthenticated,
    isFollowing,
    description,
    profileImage,
    useEmailCapture,
    ...props
}) {
    const [shown, setShown] = useState(false);
    const [following, setFollowing] = useState(isFollowing != "false");
    const wrapperRef = useRef(null);

    function displayBanner() {
        setShown(true);
        analytics.event("du_feed_banner", "banner_shown", "du_banner", true); // non interactive
        storage.remove("hideStickyBannerTimestamp");
    }

    useEffect(() => {
        let hideStickyBannerTimestamp = storage.get("hideStickyBannerTimestamp");
        let timer;
        if (
            !hideStickyBannerTimestamp ||
            hideStickyBannerTimestamp < new Date().getTime() - ONE_HOUR
        ) {
            timer = setTimeout(displayBanner, 5000);
        }

        return () => {
            clearTimeout(timer);
        };
    });

    function follow() {
        const body = { lyst_id: lystId };
        return requester("/api/users/add/", { method: "POST", body })
            .then((response) => {
                if (!response.ok || response.status !== 200) {
                    analytics.event("Follow Button", "Follow - Error", null, true);
                    Promise.reject();
                    return;
                } else {
                    setFollowing(true);
                    analytics.event("Follow Button", "Follow - Success", null, true);
                }
            })
            .catch(() => {
                analytics.event("Follow Button", "Follow - Error", null, true);
            });
    }

    function unfollow() {
        const body = { lyst_id: lystId };
        return requester("/api/users/remove/", { method: "POST", body })
            .then((response) => {
                if (!response.ok || response.status !== 200) {
                    analytics.event("Follow Button", "Unfollow - Error", null, true);
                    Promise.reject();
                    return;
                } else {
                    setFollowing(false);
                    analytics.event("Follow Button", "Unfollow - Success", null, true);
                }
            })
            .catch(() => {
                analytics.event("Follow Button", "Unfollow - Error", null, true);
            });
    }

    function onClick() {
        analytics.event("du_feed_banner", "click_banner_cta", "du_banner");
        if (isAuthenticated === "false") {
            if (useEmailCapture === "true") {
                props.openEmailCaptureOverlay({
                    designerId: lystId,
                    registrationReason: registrationReason,
                    captureType: "signup_du",
                });
            } else {
                props.openSignupOverlay({
                    path: "/account/register/",
                    queryParams: {
                        designer_id: [lystId],
                        type: [registrationReason],
                    },
                    pageType: "signup_form",
                    pageSubType: "signup_du_save",
                });
            }
        } else if (following) {
            unfollow();
        } else {
            follow();
        }
    }
    function triggerClose() {
        setShown(false);
        storage.set("hideStickyBannerTimestamp", new Date().getTime());
        analytics.event("du_feed_banner", "banner_hidden", "du_banner", true); // non interactive
    }
    const bannerClassNames = classnames(styles.stickyBanner, {
        [styles.stickyBannerShow]: shown,
    });

    const buttonLabel = following ? followedText : notFollowedText;
    let renderedProfileImage = "";
    if (profileImage) {
        renderedProfileImage = <img src={profileImage} className={styles.imageProfile} />;
    }
    return (
        <div ref={wrapperRef}>
            <div className={bannerClassNames}>
                <div className={styles.wrapper}>
                    <div>
                        {renderedProfileImage}
                        <div
                            className={styles.content}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                    <div>
                        <Button styleType="primary" className={styles.button} onClick={onClick}>
                            <span>{buttonLabel}</span>
                        </Button>
                        <button className={styles.closeButton} onClick={triggerClose}>
                            <SVGIcon name="cross" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default withReduxProvider(
    connect(
        null,
        { openSignupOverlay, openEmailCaptureOverlay }
    )(FollowStickyBanner)
);

FollowStickyBanner.propTypes = {
    lystId: PropTypes.number,
    registrationReason: PropTypes.string,
    followedText: PropTypes.string,
    notFollowedText: PropTypes.string,
    isAuthenticated: PropTypes.string,
    isFollowing: PropTypes.string,
    description: PropTypes.string,
    openSignupOverlay: PropTypes.func.isRequired,
    openEmailCaptureOverlay: PropTypes.func.isRequired,
    profileImage: PropTypes.string,
    useEmailCapture: PropTypes.string,
};
